<template>
  <div ref="root" :class="$style['adl-chart-bar']">
    <slot name="tooltipText"></slot>
    <mce-layout
      :plot-width="width"
      :plot-height="height"
      :axis-left-width="axisLeftWidth"
      :axis-right-width="axisRightWidth"
    >
      <template #legend-top>
        <mce-legend hideTopBorder :horizontal="true">
          <mce-legend-item
            v-for="(d, i) in legendItems"
            :key="i"
            :label="d.text ? d.text : d"
            :key-type="d.legendKeyType ? d.legendKeyType : legendKeyType"
            :color="color[i]"
          ></mce-legend-item>
        </mce-legend>
      </template>
      <template #axis-left>
        <mce-y-axis
          scale-type="linear"
          :labels="{ unit: 'AUD', axis: yAxisLabel }"
          :tick-quantity="tickQuantityY"
          :domain="domainY"
          :range="rangeY"
          :label-format="FormatCurrency"
        >
          <template v-if="isHideFirstTick" #mce-y-tick-0
            ><span></span
          ></template>
        </mce-y-axis>
      </template>
      <template #axis-bottom>
        <div ref="xAxisParent">
          <mce-x-axis
            scale-type="linear"
            :labels="{ axis: 'Year' }"
            :domain="domainX"
            :range="changeRangeX"
          ></mce-x-axis>
        </div>
      </template>
      <template #default>
        <mce-pointer v-model="mouse"></mce-pointer>
        <mce-grid
          :class="$style['hide-cursor']"
          scale-type-x="linear"
          scale-type-y="linear"
          :domain-x="domainX"
          :domain-y="domainY"
          :range-x="changeRangeX"
          :range-y="rangeY"
          :tickQuantityY="tickQuantityY"
          :outlines="{ top: true, bottom: true }"
        ></mce-grid>
        <mce-bar-plot-vertical-stacked
          :class="$style['hide-cursor']"
          :color="color"
          :hideGap="true"
          :paddingInner="0.05"
          :dataFocused="focusedData"
          :data="data"
          :domain-x="domainXLabel"
          :domain-y="domainY"
          :range-x="changeRangeX"
          :range-y="rangeY"
          :mouse="mouse"
          @mousemove-plot="onMousemovePlotBar"
          @mouseleave-plot="focusedData = []"
        ></mce-bar-plot-vertical-stacked>
        <mce-group
          class="mce-line-plot"
          v-if="linePlotData && linePlotData.length > 0"
        >
          <mce-path :style="lineStyles" :d="lineCoordinates" />
        </mce-group>
      </template>
    </mce-layout>
  </div>
</template>

<script>
import Vue from "vue";
import MceLayout from "@mce/layout";
import MceYAxis from "@mce/y-axis";
import MceXAxis from "@mce/x-axis";
import { MceLegend, MceLegendItem, MceLegendGroup } from "@mce/legend";
import MceBarPlotVerticalStacked from "@mce/bar-plot-vertical-stacked";

import MceGrid from "@mce/grid";
import { format } from "d3-format";
import McePointer from "@mce/pointer";
import McePath from "@mce/path";
import MceGroup from "@mce/group";
import { line } from "d3-shape";
import { scaleLinear } from "d3-scale";
import _ from "lodash";
export default Vue.extend({
  name: "AdlChartBar",
  components: {
    McePointer,
    MceLayout,
    MceYAxis,
    MceXAxis,
    MceGrid,
    MceLegend,
    MceLegendItem,
    MceLegendGroup,
    MceBarPlotVerticalStacked,
    McePath,
    MceGroup,
  },
  props: {
    data: {
      type: Array,
      required: true,
    },
    linePlotData: {
      type: Array,
    },
    lineColor: {
      type: String,
    },
    hideMouseHover: {
      type: Boolean,
    },
    color: {
      type: Array,
      default: () => [],
    },

    legendItems: {
      type: Array,
      default: () => [],
    },
    legendKeyType: {
      type: String,
      default: () => "bar",
    },
    rangeX: {
      type: Array,
      required: true,
    },
    currencyFormat: {
      type: String,
      required: true,
    },
    yAxisLabel: {
      type: String,
      required: true,
    },
    rangeY: {
      type: Array,
      required: true,
    },
    domainX: {
      type: Array,
      required: true,
    },
    domainY: {
      type: Array,
      required: true,
    },
    tickQuantityY: {
      type: Number,
      default: 5,
    },
    tickX: {
      type: Number,
      default: 0,
    },
    axisLeftWidth: {
      type: Number,
      default: 82,
    },
    axisRightWidth: {
      type: Number,
      default: 82,
    },
    height: {
      type: Number,
      default: 82,
    },
    isHideFirstTick: {
      type: Boolean,
    },
  },
  data() {
    return {
      mouse: null,
      mouseDataBar: {},
      rootHeight: 0,
      rootWidth: 0,
      focusedData: [],
      hoverHeight: 0,
    };
  },
  mounted() {
    this.onResize();
    window.addEventListener("resize", _.debounce(this.onResize, 100));
  },
  beforeDestroy() {
    window.removeEventListener("resize", _.debounce(this.onResize, 100));
  },

  computed: {
    lineStyles() {
      return {
        stroke: this.lineColor,
        fill: "none",
        strokeWidth: 2,
        strokeLinecap: "round",
        strokeLinejoin: "round",
        opacity: 0.7,
      };
    },
    width() {
      return this.rootWidth - this.axisLeftWidth;
    },
    changeRangeX() {
      return [0, this.rootWidth - this.axisRightWidth];
    },
    domainXLabel: function () {
      return [...Array(this.data.length + this.tickX).keys()];
    },
    FormatCurrency: function () {
      return format(this.currencyFormat);
    },
    xScale() {
      return scaleLinear().domain(this.domainX).range(this.changeRangeX);
    },
    yScale() {
      return scaleLinear().domain(this.domainY).range(this.rangeY);
    },
    lineCoordinates() {
      const path = line()
        .x((d) => this.xScale(d.value.x))
        .y((d) => this.yScale(d.value.y));
      return path(this.linePlotData);
    },
  },
  watch: {
    mouse: {
      async handler() {
        if (this.mouse) {
          this.$emit("setTooltipData", _.clone(this.mouseDataBar), this.mouse);
        } else {
          this.$emit("setTooltipData", null, null);
        }
      },
    },
  },

  methods: {
    onMousemovePlotBar(mouseData) {
      const { data } = mouseData;
      this.focusedData = [data._itemId];
      this.mouseDataBar = data;
    },
    onResize() {
      let rootElement = this.$refs.root;
      if (rootElement !== undefined) {
        this.rootHeight = Number(rootElement.clientHeight) || this.rootHeight;
        this.rootWidth = Number(rootElement.clientWidth) || this.rootHeight;
      }
    },
  },
});
</script>

<style lang="scss" module>
.adl-chart-bar {
  .hide-cursor {
    cursor: none !important;
  }
  .mce-bar-plot-vertical__bar--not-focused {
    opacity: 0.6 !important;
  }
  .mce-grid__y-tick {
    stroke-dasharray: 0.2 !important;
    stroke: black !important;
  }
  .mce-grid__x-tick {
    stroke-dasharray: 0.1 !important;
  }
  .mce-axis-x__tick {
    text-align: left !important;
  }
}
</style>
